// @react
import React, { useState, ReactElement, useContext, useEffect } from 'react'
// @types
import {
  GetOrdersQuery,
  GetOrdersQueryVariables,
  OrderType,
  UserUpdateOrderAutorenewalMutation,
  UserUpdateOrderAutorenewalMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { PropsType as CardProps } from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Orders'
import { PropsType as CardItemProps } from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Orders/LineItem'
import { AutoRenewalSubmitData } from 'modules/profile/components/Membership/Form'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @context
import { SnackbarContext } from 'modules/common/context/snackbar'
// @graphQL
import { useMutation, useQuery } from '@apollo/client'
import { GET_ORDERS } from 'graphql/queries/order.query'
import { USER_UPDATE_ORDER_AUTORENEWAL } from 'graphql/mutations/user.mutation'
// @libraries
import { vsprintf } from 'sprintf-js'
import strings from 'lib/constants/strings'
import config from 'constants/config'
import Helper from 'utils/Helper.class'
// @components
import Spacer from '@bit/atd.web.spacer'
import OrderCard from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Orders'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
import Typography from '@material-ui/core/Typography'
// @module components
import usePagination from 'modules/common/hooks/usePagination'
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import QueryError from 'modules/common/components/Errors/QueryError'
import AutoRenewForm from 'modules/profile/components/Membership/Form/AutoRenew'
import Modal from '@bit/atd.web.modal'
import Button from '@bit/atd.web.button'

type PropsType = {
  timeLimit?: number
  searchKeyword: string
  setCount: (count: number) => void
}
type MemberShipContext = {
  title: string
  orderNumber: string
  orderLineNumber?: number
}

const SubscriptionOrders = (props: PropsType): ReactElement => {
  /****************************************
   *  PROPS
   ****************************************/

  const { setCount, timeLimit, searchKeyword } = props

  /******************************
   *  STATE
   *******************************/

  const [
    membershipContext,
    setMembershipContext,
  ] = useState<MemberShipContext>()
  const [renewModal, setRenewModal] = useState<boolean>(false)
  const [renewEndedModal, setRenewEndedModal] = useState<boolean>(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)

  /****************************************
   *  HOOKS
   ****************************************/

  const snackbar = useContext(SnackbarContext)
  const { Pagination, paginationProps } = usePagination()

  /****************************************
   *  GQL HOOKS
   ****************************************/

  const { loading: isLoading, error: isError, data, refetch } = useQuery<
    GetOrdersQuery,
    GetOrdersQueryVariables
  >(GET_ORDERS, {
    variables: {
      input: {
        page: paginationProps.currentPage,
        pageSize: config.pageSize,
        filters: {
          orderType: OrderType.SUBSCRIPTION,
          dateRange: timeLimit,
          search: searchKeyword,
        },
      },
    },
  })

  const [updateUserOrderAutoRenewal] = useMutation<
    UserUpdateOrderAutorenewalMutation,
    UserUpdateOrderAutorenewalMutationVariables
  >(USER_UPDATE_ORDER_AUTORENEWAL)

  useEffect(() => {
    setCount && setCount(data?.orders?.meta?.count || 0)
  }, [setCount, data])

  /****************************************
   *  FUNCTIONS
   ****************************************/

  // on Auto-Renew Toggle Handlers

  const onRenewHandler = async (
    autoRenew: CardItemProps['autoRenew'],
    orderNumber: CardProps['orderNumber'],
    orderLineNumber: CardItemProps['orderLineNumber'],
    title: CardItemProps['title'],
  ) => {
    setMembershipContext({
      title: title,
      orderNumber: orderNumber,
      orderLineNumber: orderLineNumber,
    })

    if (!autoRenew) {
      setRenewModal(true)
    } else {
      setIsSubmitting(true)
      await userOrderAutorenewalUpdateHandler(
        {
          orderNumber: orderNumber,
          // @ts-ignore
          orderLineNumber: orderLineNumber,
        },
        false,
      )
      setIsSubmitting(false)
    }
  }

  // Turn off state
  const onRenewEndedConfirmHandler = () => {
    setRenewEndedModal(false)
  }

  const userOrderAutorenewalUpdateHandler = async (
    data: AutoRenewalSubmitData,
    targetAutorenewal: boolean,
  ): Promise<boolean | void> => {
    // CC type codes AE,DS,MC,VI

    /**
     * @todo revise this once endpoint reviewed for autorenewal cancel
     * endpoint returns error currently if no CC data is passed in when cancelling
     * autorenewal
     */

    const input = targetAutorenewal
      ? {
          orderNumber: data.orderNumber,
          orderLineNumber: data.orderLineNumber,
          isAutoRenewal: true,
          creditCardType: data.creditCardType!,
          creditCardNumber: data.creditCardNumber!,
          creditCardSecurityCode: data.creditCardSecurityCode!,
          nameOnCard: data.nameOnCard!,
          expirationYear: data.expirationYear!,
          expirationMonth: data.expirationMonth!,
        }
      : {
          orderNumber: data.orderNumber,
          orderLineNumber: data.orderLineNumber,
          isAutoRenewal: false,
        }

    const { errors: mutationErrors } = await updateUserOrderAutoRenewal({
      variables: {
        input,
      },
    })

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Membership Autorenewal',
        ]),
        show: true,
      })
      if (!targetAutorenewal) setRenewEndedModal(true)
      await refetch()
      return true
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
  }

  /****************************************
   *  RENDERS
   ****************************************/

  if (isError) {
    return <QueryError />
  }

  const renderContent = () => {
    if (data?.orders.data && data?.orders.data.length > 0) {
      return (
        <>
          {data?.orders.data.map((item, index: number) => {
            const totalItems = data?.orders.data.length
            const lineItems: CardProps['lineItems'] = item?.lineItems?.map(
              (order) => {
                return {
                  url: order.itemUrl,
                  imageURL: order?.imageUrl || undefined,
                  title: order.title,
                  type: order.type,
                  price: order.price,
                  autoRenew: order.autoRenew,
                  orderLineNumber: order.orderLineNumber,
                }
              },
            )

            return (
              <React.Fragment key={`${item.__typename}_${item.id}`}>
                <OrderCard
                  date={Helper.smallDateFormatter(item?.date)}
                  total={item?.totalPrice}
                  shippedTo={item?.shippedTo || ''}
                  receiptUrl={item?.receiptUrl || ''}
                  orderNumber={item?.orderNumber}
                  lineItems={lineItems}
                  onAutoRenewChange={onRenewHandler}
                />
                {index + 1 !== totalItems && <Spacer size={24} fullWidth />}
              </React.Fragment>
            )
          })}

          <PaginationWrapper>
            <Pagination
              {...paginationProps}
              count={data.orders.meta?.totalPages}
            />
          </PaginationWrapper>

          {/* Auto-Renew Payment Form Modal */}
          <AutoRenewForm
            membershipContext={membershipContext}
            renewModal={renewModal}
            setRenewModal={setRenewModal}
            renewalHandler={userOrderAutorenewalUpdateHandler}
            isMutationSubmitting={isMutationSubmitting}
          />

          {/* Auto-Renew Ended Modal */}
          <Modal
            footerNoSeparator
            headerNoSeparator
            open={renewEndedModal}
            size="medium"
            title="Your auto renewal has been ended."
          >
            <Typography paragraph>
              Auto renewal has been discontinued for the following Membership:
            </Typography>
            <Typography paragraph>
              <strong>{membershipContext?.title}</strong>
            </Typography>
            <Typography paragraph>
              If you wish to turn auto renewal back on, you can do so at any
              time.
            </Typography>
            <Button
              variant="primary"
              size="medium"
              onClick={onRenewEndedConfirmHandler}
            >
              Okay
            </Button>
          </Modal>
        </>
      )
    }
    return (
      <div>
        <h3>{strings.PROFILE.ORDERS.NO_ORDERS}</h3>
      </div>
    )
  }

  return (
    <>
      <ConditionalLoader condition={!isLoading}>
        {renderContent()}
      </ConditionalLoader>
    </>
  )
}

export default SubscriptionOrders
